/**
 * Educational Classroom Chalkboard / Whiteboard object with wooden frame and chalk tray.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface ChalkboardProps {
  width?: number;
  height?: number;
  theme?: 'green' | 'black' | 'whiteboard';
  showGrid?: boolean;
  frameColor?: string;
}

export class Chalkboard extends SVGObject {
  public props: ChalkboardProps;

  constructor(config: SVGObjectConfig & { props?: ChalkboardProps }) {
    super({ ...config, type: 'chalkboard', layer: 'stage' });
    this.props = {
      width: 900,
      height: 520,
      theme: 'green',
      showGrid: true,
      frameColor: '#854d0e',
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const w = this.props.width || 900;
    const h = this.props.height || 520;
    const frame = 16;

    // 1. Wooden Outer Frame
    const outerFrame = Renderer.createElement('rect');
    Renderer.setAttributes(outerFrame, {
      x: -w / 2 - frame,
      y: -h / 2 - frame,
      width: w + frame * 2,
      height: h + frame * 2 + 10,
      rx: 6,
      ry: 6,
      fill: this.props.frameColor || '#854d0e',
      stroke: '#713f12',
      'stroke-width': 2,
      filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.5))'
    });
    this.container.appendChild(outerFrame);

    // 2. Board Slate Background
    let boardColor = '#14532d'; // classic dark green
    if (this.props.theme === 'black') boardColor = '#18181b';
    if (this.props.theme === 'whiteboard') boardColor = '#f8fafc';

    const slate = Renderer.createElement('rect');
    Renderer.setAttributes(slate, {
      x: -w / 2,
      y: -h / 2,
      width: w,
      height: h,
      rx: 3,
      ry: 3,
      fill: boardColor
    });
    this.container.appendChild(slate);

    // 3. Subtle Math Grid
    if (this.props.showGrid && this.props.theme !== 'whiteboard') {
      const gridPath = Renderer.createElement('path');
      let gridD = '';
      const step = 40;
      for (let x = -w / 2 + step; x < w / 2; x += step) {
        gridD += `M ${x} ${-h / 2} L ${x} ${h / 2} `;
      }
      for (let y = -h / 2 + step; y < h / 2; y += step) {
        gridD += `M ${-w / 2} ${y} L ${w / 2} ${y} `;
      }
      Renderer.setAttributes(gridPath, {
        d: gridD,
        fill: 'none',
        stroke: 'rgba(255, 255, 255, 0.05)',
        'stroke-width': 1
      });
      this.container.appendChild(gridPath);
    }

    // 4. Chalk Tray at bottom
    const tray = Renderer.createElement('rect');
    Renderer.setAttributes(tray, {
      x: -w / 2 + 50,
      y: h / 2 + frame - 4,
      width: w - 100,
      height: 12,
      rx: 3,
      ry: 3,
      fill: '#a16207',
      stroke: '#713f12',
      'stroke-width': 1
    });
    this.container.appendChild(tray);

    // 5. Piece of Chalk on tray
    const chalkPiece = Renderer.createElement('rect');
    Renderer.setAttributes(chalkPiece, {
      x: -w / 2 + 100,
      y: h / 2 + frame,
      width: 25,
      height: 6,
      rx: 2,
      ry: 2,
      fill: '#ffffff'
    });
    this.container.appendChild(chalkPiece);
  }
}
